import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UploadCloud, Loader2, Layers } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function AdminBulk() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [status, setStatus] = useState("draft");
  const [entries, setEntries] = useState([]);
  const [progress, setProgress] = useState(null);
  const [creating, setCreating] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/admin/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    setProgress(0);
    try {
      const r = await api.post("/admin/upload", fd, {
        onUploadProgress: (e) => setProgress(Math.round((e.loaded / (e.total || 1)) * 100)),
      });
      const next = r.data.files.map((f) => ({
        title: f.original_filename.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " "),
        media: [{
          storage_path: f.storage_path, media_type: f.media_type, content_type: f.content_type,
          original_filename: f.original_filename, alt_text: "", display_order: 0,
        }],
      }));
      setEntries((prev) => [...prev, ...next]);
      toast.success(`${next.length} file(s) uploaded`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setProgress(null);
    }
  };

  const createAll = async () => {
    if (!categoryId) {
      toast.error("Choose a category first");
      return;
    }
    if (!entries.length) {
      toast.error("Upload some files first");
      return;
    }
    setCreating(true);
    try {
      const r = await api.post("/admin/items/bulk", { category_id: categoryId, status, items: entries });
      toast.success(`${r.data.created} work items created`);
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const inputCls =
    "bg-surface-white border border-charcoal-900/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-copper-600";

  return (
    <div>
      <h1 className="font-serif text-3xl text-charcoal-900 mb-1">Bulk Upload</h1>
      <p className="text-sm text-charcoal-500 mb-8">
        Drop a whole folder of work. Each file becomes one item — edit titles before creating. Drafts stay private until you publish them.
      </p>

      <div className="flex flex-wrap gap-4 mb-6">
        <select
          data-testid="bulk-category-select"
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          aria-label="Bulk upload category"
          className={inputCls}
        >
          <option value="">Choose category...</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select
          data-testid="bulk-status-select"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label="Bulk upload status"
          className={inputCls}
        >
          <option value="draft">Create as Drafts</option>
          <option value="published">Publish immediately</option>
        </select>
      </div>

      <div
        data-testid="bulk-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); uploadFiles(e.dataTransfer.files); }}
        onClick={() => fileRef.current?.click()}
        className={`cursor-pointer rounded-2xl border-2 border-dashed p-12 text-center transition-colors bg-surface-white ${
          dragging ? "border-copper-600 bg-copper-100/50" : "border-charcoal-900/20 hover:border-copper-600/50"
        }`}
      >
        <UploadCloud className="w-10 h-10 mx-auto text-copper-600/70" />
        <p className="mt-3 text-sm font-medium text-charcoal-800">Drag up to hundreds of files here, or browse</p>
        <p className="mt-1 text-xs text-charcoal-500">Each file becomes a separate portfolio item in the chosen category</p>
        {progress !== null && (
          <div className="mt-4 max-w-xs mx-auto">
            <div className="h-1.5 rounded-full bg-charcoal-900/10 overflow-hidden">
              <div className="h-full bg-copper-600 transition-[width]" style={{ width: `${progress}%` }} />
            </div>
            <p className="mt-1.5 font-mono text-[10px] text-charcoal-500">{progress}%</p>
          </div>
        )}
      </div>
      <input
        ref={fileRef}
        data-testid="bulk-file-input"
        type="file"
        multiple
        accept="image/*,video/mp4,video/webm,video/quicktime,application/pdf,.svg"
        className="hidden"
        onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }}
      />

      {entries.length > 0 && (
        <div className="mt-8">
          <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-charcoal-500 mb-4">
            {entries.length} item(s) ready
          </p>
          <div className="flex flex-col gap-2.5" data-testid="bulk-entry-list">
            {entries.map((entry, i) => (
              <div key={i} className="flex items-center gap-3 bg-surface-white border border-charcoal-900/10 rounded-xl p-3">
                <span className="font-mono text-[10px] text-charcoal-400 w-8 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <input
                  data-testid={`bulk-title-${i}`}
                  value={entry.title}
                  onChange={(e) => {
                    const next = [...entries];
                    next[i] = { ...next[i], title: e.target.value };
                    setEntries(next);
                  }}
                  aria-label={`Title for item ${i + 1}`}
                  className="flex-1 bg-transparent border-b border-charcoal-900/10 text-sm text-charcoal-800 focus:outline-none focus:border-copper-600 py-1"
                />
                <button
                  data-testid={`bulk-remove-${i}`}
                  onClick={() => setEntries(entries.filter((_, k) => k !== i))}
                  aria-label={`Remove item ${i + 1}`}
                  className="text-xs text-charcoal-400 hover:text-red-600 px-2"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
          <button
            data-testid="bulk-create-button"
            onClick={createAll}
            disabled={creating || progress !== null}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-charcoal-900 text-parchment text-sm font-medium px-7 py-3.5 hover:bg-copper-600 disabled:opacity-60 transition-colors"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />}
            {creating ? "Creating..." : `Create ${entries.length} item(s)`}
          </button>
        </div>
      )}
    </div>
  );
}
